import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../store';
import { selectActiveAnalysis, selectAnalysisResults, selectUIState, clearActiveAnalysis, setExportModalOpen, selectAnalysisHistory, setProgressModalOpen } from '../../store';
import AnalysisProgressModal from './AnalysisProgressModal';
import PropertyAnalysisResults from './PropertyAnalysisResults';
import PropertyAnalysisExport from './PropertyAnalysisExport';
import { 
  Alert, 
  Snackbar, 
  Button, 
  Box, 
  Typography, 
  Chip,
  useTheme 
} from '@mui/material';
import { 
  Refresh as RefreshIcon, 
  Error as ErrorIcon, 
  CheckCircle as SuccessIcon,
  Info as InfoIcon 
} from '@mui/icons-material';

const PropertyAnalysisPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const activeAnalysis = useAppSelector(selectActiveAnalysis);
  const results = useAppSelector(selectAnalysisResults);
  const uiState = useAppSelector(selectUIState);
  const analysisHistory = useAppSelector(selectAnalysisHistory);

  // Enhanced error handling and user feedback state
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRecovering, setIsRecovering] = useState(false);

  const handleProgressClose = () => {
    // Close the progress modal to allow access to results
    dispatch(setProgressModalOpen(false));
  };

  const handleResultsClose = () => {
    // Close results and clear active analysis
    dispatch(clearActiveAnalysis());
    setSuccessMessage('Analysis results closed successfully');
  };

  const handleShare = () => {
    // TODO: Implement share functionality
    console.log('Share analysis results');
    setInfoMessage('Share functionality coming soon!');
    // This could integrate with existing ShareModal or ShareGoogleDriveModal
  };

  const handleExport = () => {
    // Open export modal
    dispatch(setExportModalOpen(true));
    setInfoMessage('Export options opened');
  };

  const handleExportClose = () => {
    // Close export modal
    dispatch(setExportModalOpen(false));
  };

  // Enhanced error recovery
  const handleErrorRecovery = async () => {
    if (!activeAnalysis) return;
    
    setIsRecovering(true);
    setErrorMessage(null);
    
    try {
      // Simulate recovery process
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // TODO: Implement actual recovery logic
      console.log('Attempting to recover from error:', activeAnalysis.error);
      
      setRetryCount(prev => prev + 1);
      setSuccessMessage('Recovery attempt completed. Please try again.');
      
    } catch (error: any) {
      setErrorMessage(`Recovery failed: ${error.message}`);
    } finally {
      setIsRecovering(false);
    }
  };

  // Auto-clear messages after timeout
  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];
    
    if (errorMessage) {
      timers.push(setTimeout(() => setErrorMessage(null), 5000));
    }
    if (successMessage) {
      timers.push(setTimeout(() => setSuccessMessage(null), 3000));
    }
    if (infoMessage) {
      timers.push(setTimeout(() => setInfoMessage(null), 3000));
    }
    
    return () => timers.forEach(timer => clearTimeout(timer));
  }, [errorMessage, successMessage, infoMessage]);

  // Enhanced error monitoring
  useEffect(() => {
    if (activeAnalysis?.error && !errorMessage) {
      setErrorMessage(`Analysis error: ${activeAnalysis.error}`);
    }
  }, [activeAnalysis?.error, errorMessage]);


  return (
    <Box data-testid="property-analysis-panel">
      {/* Enhanced User Feedback and Error Recovery */}
      <Box sx={{ mb: 2 }}>
        {/* Error Display with Recovery Options */}
        {errorMessage && (
          <Alert 
            severity="error" 
            action={
              <Box display="flex" gap={1}>
                <Button
                  size="small"
                  onClick={handleErrorRecovery}
                  disabled={isRecovering}
                  startIcon={isRecovering ? <RefreshIcon /> : <RefreshIcon />}
                >
                  {isRecovering ? 'Recovering...' : 'Recover'}
                </Button>
                <Button
                  size="small"
                  onClick={() => setErrorMessage(null)}
                >
                  Dismiss
                </Button>
              </Box>
            }
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {errorMessage}
              </Typography>
              {retryCount > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Recovery attempts: {retryCount}
                </Typography>
              )}
            </Box>
          </Alert>
        )}

        {/* Success Messages */}
        {successMessage && (
          <Alert 
            severity="success" 
            onClose={() => setSuccessMessage(null)}
            sx={{ mb: 2 }}
          >
            {successMessage}
          </Alert>
        )}

        {/* Info Messages */}
        {infoMessage && (
          <Alert 
            severity="info" 
            onClose={() => setInfoMessage(null)}
            sx={{ mb: 2 }}
          >
            {infoMessage}
          </Alert>
        )}

        {/* Analysis History Summary */}
        {analysisHistory.length > 0 && (
          <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon fontSize="small" />
              Analysis History
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              <Chip 
                label={`${analysisHistory.length} analyses`} 
                size="small" 
                variant="outlined" 
                color="primary" 
              />
              <Chip 
                label={`${analysisHistory.filter(a => a.results).length} completed`} 
                size="small" 
                variant="outlined" 
                color="success" 
              />
              <Chip 
                label={`${analysisHistory.filter(a => !a.results).length} pending`} 
                size="small" 
                variant="outlined" 
                color="warning" 
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Progress Modal - shows when analysis is running */}
      <AnalysisProgressModal
        open={uiState.progressModalOpen && !!activeAnalysis}
        onClose={handleProgressClose}
      />

      {/* Results Modal - shows when analysis is complete */}
      <PropertyAnalysisResults
        open={!!results && activeAnalysis?.status === 'completed'}
        onClose={handleResultsClose}
        onShare={handleShare}
        onExport={handleExport}
      />

      {/* Export Modal - shows when user wants to export results */}
      <PropertyAnalysisExport
        open={uiState.exportModalOpen}
        onClose={handleExportClose}
      />
    </Box>
  );
};

export default PropertyAnalysisPanel;